import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppLayout from "../layouts/AppLayout";
import Chat from "../components/Chat/Chat";
import PrivateChat from "../components/Chat/PrivateChat";
import FriendsList from "../components/FriendList/FriendList";
import SearchFriend from "../components/SearchFriends/SearchFreinds";
import FriendRequests from "../components/FriendRequest/FriendRequest";
import GroupList from "../components/GroupList/GroupList";
import GroupChat from "../components/Chat/GroupChat";

import { getProfile, deleteAccount, logout } from "../services/chatService";
import { accessChat } from "../services/chatService";
import { connectSocket } from "../services/socket";

import "./css/Profile.css";

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [socket, setSocket] = useState(null);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({}); // { chatId: count }
  const [socketReady, setSocketReady] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [wakingUp, setWakingUp] = useState(false);

  const token = localStorage.getItem("token");

  // ✅ Load user profile
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    let didCancel = false;

    // ✅ timeout to show waking-up message
    const wakeTimeout = setTimeout(() => {
      if (!didCancel) setWakingUp(true);
    }, 3000); // 3 seconds

    getProfile()
      .then((res) => {
        clearTimeout(wakeTimeout);
        if (!didCancel) setUser(res.data);
      })
      .catch(() => {
        clearTimeout(wakeTimeout);
        if (!didCancel) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      });

    return () => {
      didCancel = true;
      clearTimeout(wakeTimeout);
    };
  }, [navigate, token]);

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem("token");
    navigate("/login");
  };

  // ✅ Initialize Socket.IO
  useEffect(() => {
    if (!user) return;

    const s = connectSocket(user);
    setSocket(s);

    s.on("connect", () => {
      //console.log("Socket connected:", s.id);
      setSocketReady(true);
    });

    s.on("disconnect", () => {
      console.log("Socket disconnected");
      setSocketReady(false);
    });

    s.on("unreadUpdate", ({ friendId }) => {
      setUnreadCounts((prev) => ({
        ...prev,
        [friendId]: (prev[friendId] || 0) + 1,
      }));
    });

    s.on("seenConfirmed", ({ friendId }) => {
      setUnreadCounts((prev) => ({
        ...prev,
        [friendId]: 0,
      }));
    });

    return () => {
      s.off("connect");
      s.off("disconnect");
      s.off("unreadUpdate");
      s.off("seenConfirmed");
    };
  }, [user]);

  // ✅ Open chat with friend
  const handleFriendSelect = async (friend) => {
    if (!socket || !socketReady) {
      console.warn("Socket not ready yet. Waiting...");

      // Optionally: wait until ready
      const waitForSocket = () =>
        new Promise((resolve) => {
          const check = () => {
            if (socket && socket.connected) resolve();
            else setTimeout(check, 50);
          };
          check();
        });

      await waitForSocket();
    }

    try {
      const res = await accessChat(friend._id);
      const chatId = res.data._id;

      setSelectedFriend({ ...friend, chatId });
      setSelectedGroup(null);

      socket.emit("joinRoom", chatId);
      socket.emit("markSeen", { friendId: friend._id });
    } catch (err) {
      console.error("Open chat failed", err);
    }
  };

  const handleGroupSelect = (group) => {
    if (!socketReady) return;

    setSelectedGroup(group);
    setSelectedFriend(null);

    socket.emit("joinRoom", group._id);
  };

  const handleDeleteAccount = async () => {
    const confirm = window.confirm(
      "THIS WILL PERMANENTLY DELETE ALL YOUR DATA. CONTINUE?",
    );

    if (!confirm) return;

    try {
      await deleteAccount();

      // Clean up local data & redirect
      localStorage.removeItem("token");
      navigate("/register");
    } catch (err) {
      console.error(
        "Delete account failed:",
        err.response?.data || err.message,
      );
      alert("Failed to delete account. Try again later.");
    }
  };

  if (!user) {
    return (
      <div className="loading-wrapper">
        <div className="spinner"></div>
        {wakingUp ? (
          <p>
            Server is waking up (free-tier limitation)… please wait a few
            seconds
          </p>
        ) : (
          <p>Loading…</p>
        )}
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="profile-container">
        {/* LEFT PANEL */}
        <div className="profile-left">
          <SearchFriend token={token} />

          <div className="profile-info">
            {/* The first letter of the username as an avatar */}
            <div className="profile-avatar">
              {user.username?.charAt(0).toUpperCase()}
            </div>

            <h2 className="profile-username">{user.username}</h2>
            <span className="profile-id-tag">ID: {user.id}</span>

            <div className="profile-actions">
              <button className="logout-btn" onClick={handleLogout}>
                Logout
              </button>
              <button
                className="delete-account-btn"
                onClick={handleDeleteAccount}
              >
                Delete Account
              </button>
            </div>
          </div>

          <FriendRequests token={token} />

          <FriendsList
            token={token}
            socket={socket}
            user={user}
            onFriendSelect={(friend) => {
              if (!socketReady) {
                console.warn("Socket not ready yet");
                return;
              }
              handleFriendSelect(friend);
            }}
            unreadCounts={unreadCounts}
            setUnreadCounts={setUnreadCounts}
          />

          <GroupList
            token={token}
            unreadCounts={unreadCounts}
            onGroupSelect={handleGroupSelect}
          />
        </div>

        {/* RIGHT PANEL */}
        <div className="profile-right">
          {/* GROUP CHAT */}
          {selectedGroup && socket?.connected ? (
            <div className="chat-area">
              <div className="private-chat-header">
                <button
                  className="back-btn"
                  onClick={() => setSelectedGroup(null)}
                >
                  ⬅ Back
                </button>
                <span>Group: {selectedGroup.name}</span>
              </div>

              <GroupChat
                user={user}
                group={selectedGroup}
                token={token}
                socket={socket}
              />
            </div>
          ) : selectedFriend?.chatId && socket?.connected ? (
            /* PRIVATE CHAT */
            <div className="chat-area">
              <div className="private-chat-header">
                <button
                  className="back-btn"
                  onClick={() => setSelectedFriend(null)}
                >
                  ⬅ Back
                </button>
                <span>Friend: {selectedFriend.username}</span>
              </div>

              <PrivateChat
                user={user}
                selectedFriend={selectedFriend}
                token={token}
                socket={socket}
              />
            </div>
          ) : (
            /* PUBLIC CHAT */
            <Chat user={user} socket={socket} />
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Profile;
